import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useId } from 'react';
import { useToast } from './ToastContext';
import { GOLD, NAVY_FORM } from './colors';

const API = '/api';

function EyeButton({ visible, onClick }) {
  return (
    <button
      type="button" style={s.eyeToggle}
      onClick={onClick}
      aria-label={visible ? 'Hide password' : 'Show password'}
    >
      {visible ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a18.36 18.36 0 0 1 5.06-5.94" />
          <path d="M9.9 4.24A10.4 10.4 0 0 1 12 5c7 0 11 7 11 7a18.5 18.5 0 0 1-2.16 3.19" />
          <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
      )}
    </button>
  );
}

// Mirrors backend validate_password_strength (auth.py) so the login screen can show a
// real-time checklist without waiting on a round trip.
const COMMON_PASSWORDS = new Set(['Password1!', 'Welcome1!', 'Enko2024!', 'Enko2025!', 'Enko2026!', '123456789012', 'Passw0rd!']);
const SPECIAL_CHARS_RE = /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/;
const getPasswordChecks = (pwd) => ([
  { key: 'length',  label: 'At least 12 characters',        met: pwd.length >= 12 },
  { key: 'upper',   label: 'One uppercase letter',           met: /[A-Z]/.test(pwd) },
  { key: 'lower',   label: 'One lowercase letter',           met: /[a-z]/.test(pwd) },
  { key: 'digit',   label: 'One digit',                      met: /[0-9]/.test(pwd) },
  { key: 'special', label: 'One special character',          met: SPECIAL_CHARS_RE.test(pwd) },
  { key: 'common',  label: 'Not a commonly used password',   met: pwd.length > 0 && !COMMON_PASSWORDS.has(pwd) },
]);

// FIRSTLOGIN: the server's exact message for an expired MFA step after a password change; shown
// translated. Kept in step with routers/auth.py SESSION_EXPIRED_NEW_PASSWORD.
const SESSION_EXPIRED_NEW_PASSWORD = 'Your session expired. Please sign in again with the password you just chose.';

function Login({ onLogin, initialStep, initialToken }) {
  const { t: loginT } = useTranslation();
  // FIRSTLOGIN: show the authenticator guidance after an "Invalid code".
  const [showMfaGuide, setShowMfaGuide] = useState(false);
  const localise = (detail, fallback) => (
    detail === SESSION_EXPIRED_NEW_PASSWORD ? loginT('mfaGuide.sessionExpiredNewPassword') : (detail || fallback));
  const showToast = useToast();
  const [step, setStep] = useState(initialStep || 'credentials');
  // A11YLABEL: one id prefix per component INSTANCE, so two copies of this
  // form on one page cannot produce duplicate ids.
  const a11yId = useId();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaSecret, setMfaSecret] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [loginData, setLoginData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // TOKENBOUND: why the person was signed out, when the server ended their session rather than the
  // token simply expiring. Read ONCE on mount and cleared immediately, so it explains THIS arrival
  // at the login screen and never reappears on a later visit. Without it a password change or a
  // session hitting its ceiling produced a silent reload with no explanation at all, which is
  // exactly what item 6 of this task forbids.
  const [signOutReason, setSignOutReason] = useState(null);
  useEffect(() => {
    try {
      const reason = sessionStorage.getItem('enko_signout_reason');
      if (reason) {
        setSignOutReason(reason);
        sessionStorage.removeItem('enko_signout_reason');
      }
    } catch { /* sessionStorage unavailable - no banner, the login screen still works */ }
  }, []);
  const [resetToken] = useState(initialToken || null);
  const [resetEmail, setResetEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showChangeConfirmPassword, setShowChangeConfirmPassword] = useState(false);

  // SECONDARYROLE: secondaryRole is passed through unchanged to onLogin so App.js can store it in
  // enko_auth alongside role - additive only, every existing caller/positional argument is untouched.
  const finish = (userEmail, token, role, isTestAccount, secondaryRole) => {
    onLogin(userEmail, token, role, isTestAccount, secondaryRole);
  };

  const proceedAfterPassword = async (data) => {
    if (data.mfa_required && !data.mfa_enabled) {
      setLoading(true);
      try {
        // setup-mfa is an authenticated endpoint. Before MFA is verified, /auth/login only
        // issues a short-lived MFA-pending token (kept in memory in loginData, never in
        // sessionStorage); that is the credential accepted here to generate the QR/secret.
        const res = await fetch(`${API}/auth/setup-mfa?email=${encodeURIComponent(data.email)}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${data.mfa_pending_token}` },
        });
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          setError(localise(errBody.detail, 'Failed to set up MFA. Please try again.'));
          return;
        }
        const body = await res.json();
        setMfaSecret(body.secret);
        setQrCode(body.qr_code || '');
        setStep('mfa_setup');
      } catch {
        setError('Failed to set up MFA. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }
    if (data.mfa_required && data.mfa_enabled) {
      setStep('mfa_verify');
      return;
    }
    finish(data.email, data.access_token, data.role, data.is_test_account, data.secondary_role);
  };

  const handleLogin = async () => {
    if (!email || !password) { setError('Please enter your email and password.'); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const body = await res.json();
        setError(body.detail || 'Invalid email or password.');
        return;
      }
      const data = await res.json();
      setLoginData(data);
      // Strong-password policy takes priority over the temp-password flow - an account can
      // carry both flags (e.g. every pre-existing account right after the policy migration),
      // and this is the stronger, newer requirement of the two.
      if (data.must_change_password) {
        setStep('must_change_password');
        return;
      }
      if (data.is_temp_password) {
        setStep('change_password');
        return;
      }
      await proceedAfterPassword(data);
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (!getPasswordChecks(newPassword).every(c => c.met)) { setError('Please meet all password requirements.'); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, current_password: password, new_password: newPassword }),
      });
      if (!res.ok) {
        const body = await res.json();
        setError(body.detail || 'Failed to change password.');
        return;
      }
      // FIRSTLOGIN: the server issues a FRESH MFA token with the password change, so the MFA step
      // gets its full window from now rather than from the first sign-in.
      const changed = await res.json().catch(() => ({}));
      showToast('Password updated successfully', 'success');
      await proceedAfterPassword(changed && changed.mfa_pending_token
        ? { ...loginData, mfa_pending_token: changed.mfa_pending_token } : loginData);
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMustChangePassword = async () => {
    if (!getPasswordChecks(newPassword).every(c => c.met)) { setError('Please meet all password requirements.'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/auth/change-password-required`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token: loginData?.temp_token, new_password: newPassword }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.detail || 'Failed to change password.');
        return;
      }
      const data = await res.json();
      setLoginData(data);
      showToast('Password updated successfully', 'success');
      await proceedAfterPassword(data);
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMfa = async () => {
    if (!mfaCode || mfaCode.length !== 6) { setError('Please enter the 6-digit code.'); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/auth/verify-mfa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Send the in-memory MFA-pending token alongside the TOTP code so the backend can
        // confirm this verification belongs to a completed password step.
        body: JSON.stringify({ email, token: mfaCode, mfa_pending_token: loginData?.mfa_pending_token }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (body.detail === 'Invalid code') {
          setError(loginT('mfaGuide.invalidCode'));
          setShowMfaGuide(true);
          return;
        }
        setError(localise(body.detail, 'Invalid code. Please try again.'));
        return;
      }
      const data = await res.json();
      // Use the fresh token issued by verify-mfa rather than reusing the earlier login token.
      if (!data.access_token) {
        setError('Login failed. Please try again.');
        return;
      }
      finish(email, data.access_token, data.role, data.is_test_account, data.secondary_role);
    } catch {
      // Surface verify/finish failures instead of leaving the user on a silent screen.
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail) { setError('Please enter your email address.'); return; }
    setLoading(true);
    setError(null);
    try {
      await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail }),
      });
      setStep('forgot_sent');
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (!getPasswordChecks(newPassword).every(c => c.met)) { setError('Please meet all password requirements.'); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API}/auth/reset-password`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: resetToken, new_password: newPassword }),
        }
      );
      if (!res.ok) {
        const body = await res.json();
        setError(body.detail || 'Reset failed. The link may have expired.');
        return;
      }
      showToast('Password updated successfully', 'success');
      setStep('reset_done');
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    if (step === 'credentials') {
      return (
        <>
          <h2 style={s.title}>Welcome back</h2>
          <p style={s.subtitle}>Sign in with your Enko Capital credentials</p>
          {signOutReason && (
            <div style={{ fontSize: '13px', color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '6px', padding: '10px 12px', marginBottom: '12px', lineHeight: 1.5 }} role="status" aria-live="polite">
              {signOutReason}
            </div>
          )}
          {error && <div style={s.error}>{error}</div>}
          <div style={s.field}>
            <label style={s.label} htmlFor={`${a11yId}-email-address`}>Email address</label>
            
            <input id={`${a11yId}-email-address`}
              style={s.input} type="email" value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="yourname@enkocapital.com" autoFocus
            />
          </div>
          <div style={s.field}>
            <label style={s.label}>Password</label>
            <div style={s.inputWrapper}>
              <input
                style={s.inputWithToggle} type={showPassword ? 'text' : 'password'} value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
                placeholder="••••••••"
              />
              <EyeButton visible={showPassword} onClick={() => setShowPassword(v => !v)} />
            </div>
          </div>
          <button style={s.btn} onClick={handleLogin} disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
          <button style={s.forgotBtn} onClick={() => { setError(null); setStep('forgot_password'); }}>
            Forgot your password?
          </button>
          <div style={s.securityFooter}>
            <div style={s.dividerLine} />
            <div style={s.securityRow}>
              <svg width="13" height="15" viewBox="0 0 13 15" fill="none" style={{ flexShrink: 0 }}>
                <path d="M6.5 0.5L1 2.8v4.2c0 3.5 2.5 6.8 5.5 7.5C9.5 13.8 12 10.5 12 7V2.8L6.5 0.5z" fill={GOLD} />
              </svg>
              <span style={s.securityText}>Protected by two-factor authentication</span>
            </div>
          </div>
        </>
      );
    }

    if (step === 'forgot_password') {
      return (
        <>
          <h2 style={s.title}>Reset password</h2>
          <p style={s.subtitle}>Enter your Enko Capital email and we'll send you a reset link.</p>
          {error && <div style={s.error}>{error}</div>}
          <div style={s.field}>
            <label style={s.label} htmlFor={`${a11yId}-email-address-2`}>Email address</label>
            
            <input id={`${a11yId}-email-address-2`}
              style={s.input} type="email" value={resetEmail}
              onChange={e => setResetEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleForgotPassword()}
              placeholder="yourname@enkocapital.com" autoFocus
            />
          </div>
          <button style={s.btn} onClick={handleForgotPassword} disabled={loading}>
            {loading ? 'Sending…' : 'Send reset link'}
          </button>
          <button style={s.forgotBtn} onClick={() => { setError(null); setStep('credentials'); }}>
            Back to sign in
          </button>
        </>
      );
    }

    if (step === 'forgot_sent') {
      return (
        <>
          <h2 style={s.title}>Check your email</h2>
          <p style={s.subtitle}>
            If an account exists for <strong>{resetEmail}</strong>, a reset link has been sent.
            Check your inbox and follow the instructions.
          </p>
          <button style={s.btn} onClick={() => setStep('credentials')}>Back to sign in</button>
        </>
      );
    }

    if (step === 'reset_password') {
      const checks = getPasswordChecks(newPassword);
      const allMet = checks.every(c => c.met);
      const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
      return (
        <>
          <h2 style={s.title}>Set a new password</h2>
          <p style={s.subtitle}>Choose a strong password that meets our security policy.</p>
          {error && <div style={s.error}>{error}</div>}
          <div style={s.field}>
            <label style={s.label}>New password</label>
            <div style={s.inputWrapper}>
              <input
                style={s.inputWithToggle} type={showResetPassword ? 'text' : 'password'} value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="At least 12 characters" autoFocus
              />
              <EyeButton visible={showResetPassword} onClick={() => setShowResetPassword(v => !v)} />
            </div>
          </div>
          <ul style={s.strengthList}>
            {checks.map(c => (
              <li key={c.key} style={{ ...s.strengthItem, color: c.met ? '#2e8b57' : '#c0392b' }}>
                <span style={s.strengthIcon}>{c.met ? '✓' : '-'}</span>{c.label}
              </li>
            ))}
          </ul>
          <div style={s.field}>
            <label style={s.label}>Confirm password</label>
            <div style={s.inputWrapper}>
              <input
                style={s.inputWithToggle} type={showResetConfirmPassword ? 'text' : 'password'} value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleResetPassword()}
              />
              <EyeButton visible={showResetConfirmPassword} onClick={() => setShowResetConfirmPassword(v => !v)} />
            </div>
          </div>
          <button style={s.btn} onClick={handleResetPassword} disabled={loading || !allMet || !passwordsMatch}>
            {loading ? 'Saving…' : 'Set new password'}
          </button>
        </>
      );
    }

    if (step === 'reset_done') {
      return (
        <>
          <h2 style={s.title}>Password updated</h2>
          <p style={s.subtitle}>Your password has been changed successfully. You can now sign in.</p>
          <button style={s.btn} onClick={() => setStep('credentials')}>Sign in</button>
        </>
      );
    }

    if (step === 'change_password') {
      const checks = getPasswordChecks(newPassword);
      const allMet = checks.every(c => c.met);
      const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
      return (
        <>
          <h2 style={s.title}>Set your password</h2>
          <p style={s.subtitle}>Your account was created with a temporary password. Please choose a new one before continuing.</p>
          {error && <div style={s.error}>{error}</div>}
          <div style={s.field}>
            <label style={s.label}>New password</label>
            <div style={s.inputWrapper}>
              <input
                style={s.inputWithToggle} type={showChangePassword ? 'text' : 'password'} value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="At least 12 characters" autoFocus
              />
              <EyeButton visible={showChangePassword} onClick={() => setShowChangePassword(v => !v)} />
            </div>
          </div>
          <ul style={s.strengthList}>
            {checks.map(c => (
              <li key={c.key} style={{ ...s.strengthItem, color: c.met ? '#2e8b57' : '#c0392b' }}>
                <span style={s.strengthIcon}>{c.met ? '✓' : '-'}</span>{c.label}
              </li>
            ))}
          </ul>
          <div style={s.field}>
            <label style={s.label}>Confirm password</label>
            <div style={s.inputWrapper}>
              <input
                style={s.inputWithToggle} type={showChangeConfirmPassword ? 'text' : 'password'} value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleChangePassword()}
              />
              <EyeButton visible={showChangeConfirmPassword} onClick={() => setShowChangeConfirmPassword(v => !v)} />
            </div>
          </div>
          <button style={s.btn} onClick={handleChangePassword} disabled={loading || !allMet || !passwordsMatch}>
            {loading ? 'Saving…' : 'Set password'}
          </button>
        </>
      );
    }

    if (step === 'must_change_password') {
      const checks = getPasswordChecks(newPassword);
      const allMet = checks.every(c => c.met);
      const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
      return (
        <>
          <h2 style={s.title}>Update your password</h2>
          <p style={s.subtitle}>For your security, please set a new password that meets our updated policy before continuing.</p>
          {error && <div style={s.error}>{error}</div>}
          <div style={s.field}>
            <label style={s.label}>New password</label>
            <div style={s.inputWrapper}>
              <input
                style={s.inputWithToggle} type={showNewPassword ? 'text' : 'password'} value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="At least 12 characters" autoFocus
              />
              <EyeButton visible={showNewPassword} onClick={() => setShowNewPassword(v => !v)} />
            </div>
          </div>
          <ul style={s.strengthList}>
            {checks.map(c => (
              <li key={c.key} style={{ ...s.strengthItem, color: c.met ? '#2e8b57' : '#c0392b' }}>
                <span style={s.strengthIcon}>{c.met ? '✓' : '-'}</span>{c.label}
              </li>
            ))}
          </ul>
          <div style={s.field}>
            <label style={s.label}>Confirm password</label>
            <div style={s.inputWrapper}>
              <input
                style={s.inputWithToggle} type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleMustChangePassword()}
              />
              <EyeButton visible={showConfirmPassword} onClick={() => setShowConfirmPassword(v => !v)} />
            </div>
          </div>
          <button style={s.btn} onClick={handleMustChangePassword} disabled={loading || !allMet || !passwordsMatch}>
            {loading ? 'Saving…' : 'Update password'}
          </button>
        </>
      );
    }

    if (step === 'mfa_setup') {
      return (
        <>
          <h2 style={s.title}>Set up authenticator</h2>
          <p style={s.subtitle}>
            Scan the QR code with Google Authenticator, Authy, or a similar app.
          </p>
          {/* FIRSTLOGIN: old entries are the commonest reason codes are refused later. */}
          <p data-testid="mfa-guide" role="note" style={{ ...s.subtitle, fontWeight: 600 }}>
            {loginT('mfaGuide.beforeScan')}
          </p>
          {error && <div style={s.error}>{error}</div>}
          {qrCode && (
            <div style={s.qrWrapper}>
              <p style={s.qrLabel}>Scan this QR code with your authenticator app</p>
              <img
                src={`data:image/png;base64,${qrCode}`}
                alt="Scan with your authenticator app"
                style={s.qrImage}
              />
            </div>
          )}
          <div style={s.secretBox}>
            <p style={s.secretRow}><span style={s.secretLabel}>Account:</span> Enko Portal</p>
            <p style={s.secretRow}><span style={s.secretLabel}>Email:</span> {email}</p>
            <p style={s.secretRow}><span style={s.secretLabel}>Or enter key manually:</span></p>
            <code style={s.secret}>{mfaSecret}</code>
          </div>
          <div style={s.field}>
            <label style={s.label} htmlFor={`${a11yId}-enter-the-6-digit-code-to-confirm-se`}>Enter the 6-digit code to confirm setup</label>
            
            <input id={`${a11yId}-enter-the-6-digit-code-to-confirm-se`}
              style={{ ...s.input, letterSpacing: '0.2em', textAlign: 'center' }}
              type="text" inputMode="numeric" maxLength={6}
              value={mfaCode}
              onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={e => e.key === 'Enter' && handleVerifyMfa()}
              placeholder="000000" autoFocus
            />
          </div>
          <button style={s.btn} onClick={handleVerifyMfa} disabled={loading}>
            {loading ? 'Verifying…' : 'Confirm & continue'}
          </button>
        </>
      );
    }

    if (step === 'mfa_verify') {
      return (
        <>
          <h2 style={s.title}>Two-factor authentication</h2>
          <p style={s.subtitle}>Enter the 6-digit code from your authenticator app.</p>
          {error && <div style={s.error}>{error}</div>}
          {showMfaGuide && (
            <p data-testid="mfa-guide-after-error" role="note" style={{ ...s.subtitle, fontWeight: 600 }}>
              {loginT('mfaGuide.beforeScan')}
            </p>
          )}
          <div style={s.field}>
            <label style={s.label} htmlFor={`${a11yId}-authentication-code`}>Authentication code</label>
            
            <input id={`${a11yId}-authentication-code`}
              style={{ ...s.input, letterSpacing: '0.2em', textAlign: 'center' }}
              type="text" inputMode="numeric" maxLength={6}
              value={mfaCode}
              onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={e => e.key === 'Enter' && handleVerifyMfa()}
              placeholder="000000" autoFocus
            />
          </div>
          <button style={s.btn} onClick={handleVerifyMfa} disabled={loading}>
            {loading ? 'Verifying…' : 'Verify'}
          </button>
        </>
      );
    }
  };

  return (
    <div style={s.page}>
      {/* Left panel - branding */}
      <div style={s.leftPanel}>
        <div style={s.brand}>
          <div style={s.sigmaBox}>Σ</div>
          <div>
            <div style={s.portalName}>Enko Portal</div>
            <div style={s.companyName}>Enko Capital Management</div>
          </div>
        </div>
        <div style={s.taglineBlock}>
          <div style={s.goldLine} />
          <div style={s.taglineText}>Internal operations platform</div>
        </div>
      </div>

      {/* Right panel - form */}
      <div style={s.rightPanel}>
        <div style={s.formWrapper}>
          {renderStep()}
        </div>
      </div>
    </div>
  );
}

const s = {
  page: {
    display: 'flex',
    height: '100vh',
    backgroundColor: '#FAFAF8',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    overflow: 'hidden',
  },

  /* ── Left panel ── */
  leftPanel: {
    width: '28%',
    backgroundColor: '#F5F0E8',
    borderRight: '0.5px solid #E8DFC8',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '48px',
    boxSizing: 'border-box',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  },
  sigmaBox: {
    width: '44px',
    height: '44px',
    border: `1.5px solid ${GOLD}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: GOLD,
    fontSize: '22px',
    fontWeight: '300',
    flexShrink: 0,
    fontFamily: 'Georgia, serif',
  },
  portalName: {
    fontSize: '18px',
    fontWeight: '500',
    color: NAVY_FORM,
    lineHeight: '1.2',
  },
  companyName: {
    fontSize: '12px',
    color: '#aaa',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    marginTop: '2px',
  },
  taglineBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  goldLine: {
    width: '32px',
    height: '2px',
    backgroundColor: GOLD,
  },
  taglineText: {
    fontSize: '12px',
    color: '#999',
    letterSpacing: '0.02em',
  },

  /* ── Right panel ── */
  rightPanel: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 40px',
    overflowY: 'auto',
  },
  formWrapper: {
    width: '100%',
    maxWidth: '340px',
  },

  /* ── Form elements ── */
  title: {
    fontSize: '22px',
    fontWeight: '500',
    color: NAVY_FORM,
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '13px',
    color: '#999',
    margin: '0 0 28px 0',
    lineHeight: '1.5',
  },
  field: {
    textAlign: 'left',
    marginBottom: '18px',
  },
  label: {
    display: 'block',
    fontSize: '11px',
    fontWeight: '500',
    color: '#888',
    marginBottom: '7px',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    border: '1px solid #e0dbd0',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
    backgroundColor: '#fff',
    color: NAVY_FORM,
  },
  inputWrapper: {
    position: 'relative',
  },
  inputWithToggle: {
    width: '100%',
    padding: '11px 40px 11px 14px',
    border: '1px solid #e0dbd0',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
    backgroundColor: '#fff',
    color: NAVY_FORM,
  },
  eyeToggle: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    color: '#999',
  },
  btn: {
    width: '100%',
    backgroundColor: GOLD,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '12px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    marginTop: '8px',
    letterSpacing: '0.02em',
  },
  forgotBtn: {
    width: '100%',
    background: 'none',
    border: 'none',
    color: '#aaa',
    fontSize: '13px',
    cursor: 'pointer',
    marginTop: '14px',
    textAlign: 'center',
    display: 'block',
    padding: '0',
  },
  error: {
    backgroundColor: '#fdf3f3',
    border: '1px solid #e8c8c8',
    borderRadius: '6px',
    padding: '10px 14px',
    fontSize: '13px',
    color: '#c0392b',
    marginBottom: '18px',
    textAlign: 'left',
  },

  /* ── Password strength checklist (must_change_password step) ── */
  strengthList: {
    listStyle: 'none',
    padding: 0,
    margin: '-6px 0 18px 0',
    textAlign: 'left',
  },
  strengthItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    fontSize: '12px',
    marginBottom: '5px',
  },
  strengthIcon: {
    display: 'inline-flex',
    width: '14px',
    justifyContent: 'center',
    flexShrink: 0,
  },

  /* ── Security footer (credentials step only) ── */
  securityFooter: {
    marginTop: '32px',
  },
  dividerLine: {
    height: '1px',
    backgroundColor: '#ece8e0',
    marginBottom: '16px',
  },
  securityRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '7px',
  },
  securityText: {
    fontSize: '12px',
    color: '#667085',
  },

  /* ── MFA / QR ── */
  qrWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '20px',
  },
  qrLabel: {
    fontSize: '13px',
    color: '#555',
    marginBottom: '12px',
  },
  qrImage: {
    width: '180px',
    height: '180px',
    borderRadius: '8px',
    border: '1px solid #e0dbd0',
  },
  secretBox: {
    backgroundColor: '#f9f6f0',
    border: '1px solid #e8dfc8',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '20px',
    textAlign: 'left',
  },
  secretRow: {
    margin: '0 0 6px 0',
    fontSize: '13px',
    color: '#555',
  },
  secretLabel: {
    fontWeight: '600',
    color: '#333',
  },
  secret: {
    display: 'block',
    fontFamily: 'monospace',
    fontSize: '14px',
    letterSpacing: '0.15em',
    color: NAVY_FORM,
    backgroundColor: '#ede8de',
    borderRadius: '6px',
    padding: '10px 12px',
    marginTop: '4px',
    wordBreak: 'break-all',
  },
};

export default Login;
